import { useCallback, useMemo, useState } from "react";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { Pressable, StyleSheet, useWindowDimensions, View } from "react-native";

import { Todo } from "../../../graphql/generated/graphql";
import { RootStackParamList } from "../../../navigation/AppNavigator.web";
import { colors, radii, semanticColors, spacing, typographyScale } from "../../../styles/design-system";
import { ArrowRight, ListTodo } from "lucide-react";
import CustomText from "../../common/general/CustomText/CustomText";

export const UpcomingTodos = ({ todos, onSelectTodo }: { todos: Todo[]; onSelectTodo?: (todo: Todo) => void }) => {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { width } = useWindowDimensions();
  const isMedium = width >= 768;

  const handleViewAllClick = useCallback(() => {
    navigation.navigate("To do");
  }, [navigation]);

  const [hover, setHover] = useState<string | undefined>();
  const [viewAllHover, setViewAllHover] = useState(false);

  const displayTodos = useMemo(
    () => [...todos].sort((a, b) => new Date(a.date ?? "").getTime() - new Date(b.date ?? "").getTime()).slice(0, 3),
    [todos]
  );

  const headerIconSize = isMedium ? 20 : 18;
  const itemIconSize = 16;

  return (
    <View style={[styles.container, isMedium && styles.containerMd]}>
      <View style={[styles.header, isMedium && styles.headerMd]}>
        <View style={styles.headerTitle}>
          <ListTodo size={headerIconSize} color={colors.grey[600]} />
          <CustomText style={styles.headerText} text="Upcoming To-Dos" numberOfLines={1} ellipsizeMode="tail" />
        </View>
        <Pressable
          onPress={handleViewAllClick}
          onMouseEnter={() => setViewAllHover(true)}
          onMouseLeave={() => setViewAllHover(false)}
          accessibilityRole="button"
          accessibilityLabel="View all to-dos">
          <CustomText
            style={[styles.viewAllText, viewAllHover && styles.viewAllTextHover]}
            text="View all"
            useTranslationText={false}
            numberOfLines={1}
            ellipsizeMode="tail"
          />
        </Pressable>
      </View>
      {displayTodos.length > 0 ? (
        <View style={[styles.list, isMedium && styles.listMd]}>
          {displayTodos.map((todo) => (
            <Pressable
              key={todo.id}
              style={[styles.listItem, isMedium && styles.listItemMd, hover === todo.id && styles.activeItem]}
              onPress={() => onSelectTodo?.(todo)}
              onMouseEnter={() => setHover(todo.id)}
              onMouseLeave={() => setHover(undefined)}>
              {isMedium && <ListTodo size={itemIconSize} color={colors.grey[500]} />}
              <View style={styles.itemContent}>
                <CustomText
                  style={[styles.itemName, hover === todo.id && styles.activeItemName]}
                  text={todo.name}
                  useTranslationText={false}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                />
                <CustomText
                  style={styles.itemTime}
                  text={
                    todo.date
                      ? `Due ${new Date(todo.date).toLocaleString(undefined, {
                          weekday: "long",
                          month: "short",
                          day: "numeric",
                        })}`
                      : "No due date"
                  }
                  useTranslationText={false}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                />
              </View>
              {isMedium && (
                <ArrowRight size={16} color={colors.grey[400]} style={{ opacity: hover === todo.id ? 1 : 0 }} />
              )}
            </Pressable>
          ))}
        </View>
      ) : (
        <View style={styles.emptyState}>
          <ListTodo size={28} color={colors.grey[300]} />
          <CustomText style={styles.emptyTitle} text="No upcoming to-dos" useTranslationText={false} />
          <CustomText style={styles.emptySubtitle} text="Your to-dos will appear here" useTranslationText={false} />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignSelf: "stretch",
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: "rgba(208, 213, 221, 0.5)",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    padding: spacing.sm,
    shadowColor: colors.grey[900],
    shadowOffset: {
      width: 0,
      height: 24,
    },
    shadowOpacity: 0.18,
    shadowRadius: 48,
    overflow: "hidden",
    // @ts-expect-error - web-only blur
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
  },
  containerMd: {
    padding: spacing.md,
    maxWidth: 320,
    minWidth: 260,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  headerTitle: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  headerMd: {
    marginBottom: spacing.sm,
  },
  headerText: {
    color: colors.grey[900],
    fontSize: typographyScale.textSm.fontSize,
    fontWeight: 600,
  },
  viewAllText: {
    color: colors.grey[500],
    fontSize: typographyScale.textXs.fontSize,
    fontWeight: 500,
  },
  viewAllTextHover: {
    color: colors.brand[500],
  },
  list: {
    padding: spacing.xs,
    display: "flex",
    gap: spacing.xs,
    height: 300,
  },
  listMd: {
    height: "auto",
  },
  listItem: {
    display: "flex",
    flexDirection: "row",
    gap: spacing.xs,
    borderRadius: radii.md,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs,
    marginHorizontal: -spacing.xs,
    cursor: "pointer",
  },
  listItemMd: {
    gap: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    marginHorizontal: -12,
  },
  activeItem: {
    backgroundColor: colors.grey[50],
  },
  itemContent: {
    flex: 1,
  },
  itemName: {
    color: colors.grey[900],
    fontSize: typographyScale.textSm.fontSize,
    fontWeight: 500,
    lineHeight: 21,
  },
  activeItemName: {
    color: semanticColors.typography.textBrand,
  },
  itemTime: {
    color: semanticColors.typography.textLight,
    fontSize: typographyScale.textXs.fontSize,
  },
  emptyState: {
    alignItems: "center",
    paddingVertical: spacing.lg,
    gap: 6,
  },
  emptyTitle: {
    color: colors.grey[500],
    fontSize: typographyScale.textSm.fontSize,
    fontWeight: 500,
  },
  emptySubtitle: {
    color: colors.grey[400],
    fontSize: typographyScale.textXs.fontSize,
  },
});
